import type { SearchProvider, UserSettings } from '@flame/shared';
import type { Link } from '../api';

// Flame's built-in providers. `l` (local) filters your own links.
export const DEFAULT_PROVIDERS: SearchProvider[] = [
  { name: 'Local search', prefix: 'l', template: 'https://local.invalid/' },
  { name: 'DuckDuckGo', prefix: 'd', template: 'https://duckduckgo.com/?q={q}' },
  { name: 'Google', prefix: 'g', template: 'https://www.google.com/search?q={q}' },
  { name: 'Wikipedia', prefix: 'w', template: 'https://en.wikipedia.org/w/index.php?search={q}' },
  { name: 'YouTube', prefix: 'yt', template: 'https://www.youtube.com/results?search_query={q}' },
  { name: 'Reddit', prefix: 'r', template: 'https://www.reddit.com/search?q={q}' },
  { name: 'GitHub', prefix: 'gh', template: 'https://github.com/search?q={q}' },
  { name: 'IMDb', prefix: 'im', template: 'https://www.imdb.com/find?q={q}' },
  { name: 'The Movie Database', prefix: 'mv', template: 'https://www.themoviedb.org/search?query={q}' },
  { name: 'Spotify', prefix: 'sp', template: 'https://open.spotify.com/search/{q}' },
  { name: 'Disroot', prefix: 'ds', template: 'https://search.disroot.org/search?q={q}' },
];

export const LOCAL_PREFIX = 'l';

/** User providers override built-ins with the same prefix. */
export function allProviders(settings: UserSettings): SearchProvider[] {
  const custom = settings.search.providers;
  const prefixes = new Set(custom.map((p) => p.prefix.toLowerCase()));
  return [...custom, ...DEFAULT_PROVIDERS.filter((p) => !prefixes.has(p.prefix))];
}

export function searchUrl(provider: SearchProvider, query: string): string {
  const q = encodeURIComponent(query);
  return provider.template.includes('{q}')
    ? provider.template.replaceAll('{q}', q)
    : provider.template + q;
}

/** Something the user typed that should open directly rather than search. */
export function asUrl(input: string): string | null {
  const s = input.trim();
  if (/\s/.test(s) || s.length < 4) return null;
  if (/^https?:\/\//i.test(s)) return s;
  if (/^(localhost|[\w-]+(\.[\w-]+)+)(:\d+)?(\/.*)?$/i.test(s) && /\.[a-z]{2,}(?::\d+)?(\/|$)|^localhost/i.test(s)) {
    return `https://${s}`;
  }
  return null;
}

export interface ParsedQuery {
  query: string;
  /** Set when the user typed "/prefix query" with a known prefix. */
  provider: SearchProvider | null;
}

export function parseQuery(input: string, settings: UserSettings): ParsedQuery {
  const m = input.match(/^\/([a-z0-9]{1,8})\s+(.*)$/i);
  if (m) {
    const provider = allProviders(settings).find((p) => p.prefix === m[1]!.toLowerCase());
    if (provider) return { query: m[2]!, provider };
  }
  return { query: input, provider: null };
}

/** Links matching every word, best first. */
export function matchLinks(links: Link[], query: string, limit = 8): Link[] {
  const terms = query.toLowerCase().split(/\s+/).filter(Boolean);
  if (terms.length === 0) return [];
  const q = query.toLowerCase().trim();
  const scored: { link: Link; score: number }[] = [];
  for (const link of links) {
    const title = link.title.toLowerCase();
    const haystack = `${title} ${link.url.toLowerCase()} ${link.tags.join(' ')} ${link.description.toLowerCase()}`;
    if (!terms.every((t) => haystack.includes(t))) continue;
    let score = 1;
    if (title.startsWith(q)) score += 4;
    else if (title.includes(q)) score += 2;
    if (link.tags.some((t) => terms.includes(t))) score += 1;
    if (link.pinned) score += 0.5;
    scored.push({ link, score });
  }
  return scored
    .sort((a, b) => b.score - a.score || a.link.title.localeCompare(b.link.title))
    .slice(0, limit)
    .map((s) => s.link);
}

export type SearchAction =
  | { kind: 'open'; url: string; label: string }
  | { kind: 'link'; link: Link }
  | { kind: 'none' };

/** What Enter does for the current input (with `selected` local result, if any). */
export function enterAction(
  input: string,
  settings: UserSettings,
  matches: Link[],
  selected: number
): SearchAction {
  if (!input.trim()) return { kind: 'none' };
  const { query, provider } = parseQuery(input, settings);
  if (provider && provider.prefix !== LOCAL_PREFIX) {
    return { kind: 'open', url: searchUrl(provider, query), label: `Search ${provider.name}` };
  }
  if (matches[selected]) return { kind: 'link', link: matches[selected]! };
  if (provider) return { kind: 'none' }; // explicit "/l" with no matches
  const url = asUrl(input);
  if (url) return { kind: 'open', url, label: `Open ${url}` };

  const providers = allProviders(settings);
  const prefix =
    settings.search.defaultPrefix === LOCAL_PREFIX
      ? settings.search.secondaryPrefix
      : settings.search.defaultPrefix;
  const fallback = providers.find((p) => p.prefix === prefix && p.prefix !== LOCAL_PREFIX);
  return fallback
    ? { kind: 'open', url: searchUrl(fallback, query), label: `Search ${fallback.name}` }
    : { kind: 'none' };
}
